/**
 * Shared share-code helpers.
 *
 * A share code is an 8-character alphanumeric string split visually after the
 * 4th character (e.g. `3J2L-B5EB`). Everywhere in the app that shows or
 * consumes a share code should go through these two helpers so the format
 * stays consistent.
 */
/** Strip non-alphanumerics, uppercase, truncate to 8 chars. */
export function normalizeShareCode(raw) {
    return (raw !== null && raw !== void 0 ? raw : '')
        .replace(/[^A-Za-z0-9]/g, '')
        .toUpperCase()
        .slice(0, 8);
}
/**
 * Display format: uppercase alphanumerics + a separator inserted after the
 * 4th character. Defaults to a single hyphen (`3J2L-B5EB`). Pass ` - ` for
 * the spaced form used inside the EnterKey input.
 */
export function formatShareCode(raw, separator) {
    if (separator === void 0) { separator = '-'; }
    var cleaned = normalizeShareCode(raw);
    if (cleaned.length <= 4)
        return cleaned;
    return cleaned.slice(0, 4) + separator + cleaned.slice(4, 8);
}
/**
 * Does this string have the shape of a share code?
 *
 * A share code is exactly 8 alphanumerics, optionally split after the 4th by a
 * single hyphen or space (`SXAX-YSZ7`, `SXAXYSZ7`, `SXAX YSZ7`).
 *
 * This exists because the share code is carried as a BARE query key
 * (`/display-materials/<slug>?SXAX-YSZ7`), so any other query parameter sitting
 * in front of it can be mistaken for the code. `normalizeShareCode` makes that
 * worse rather than better: it strips separators and truncates to 8, so an LMS
 * tracking parameter is silently *manufactured* into a plausible-looking code
 * (`utm_source` -> `UTMS-OURC`). The nonexistent code then resolves to nothing
 * and the viewer gets the activity's default state with no warning.
 *
 * Reported from the field: a teacher shared a work-place link through
 * Schoology; students whose click-through added a parameter ahead of the code
 * silently lost her saved setup.
 *
 * NOTE: shape alone is not enough at the call site. `redirect` is eight
 * alphanumerics and passes this test, and the app's own login bounce uses
 * `?redirect=...`. Callers must ALSO require a bare key (empty value), which is
 * how the app emits codes — see PlayerMainContainer.onInit.
 */
export function isShareCodeShape(raw) {
    return /^[A-Za-z0-9]{4}[-\s]?[A-Za-z0-9]{4}$/.test((raw !== null && raw !== void 0 ? raw : '').trim());
}
